<template>
  <div class="container mx-auto py-8">
    <div class="flex items-center justify-between mb-8">
      <h1 class="text-3xl font-bold">Explora nuestros viajes</h1>
      <NuxtLink to="/my_trips/saved" class="btn btn-outline">
        <Icon name="heroicons:bookmark" class="mr-2" />
        Mis viajes guardados
      </NuxtLink>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-32">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <div v-else-if="trips.length === 0" class="text-center">
      <div class="alert alert-info">
        <Icon name="heroicons:information-circle" />
        <div>
          <h3 class="font-bold">No hay viajes disponibles</h3>
          <p>No hay viajes disponibles en este momento.</p>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- Filter tabs -->
      <div class="tabs tabs-boxed justify-center mb-8">
        <button
          class="tab"
          :class="{ 'tab-active': filterType === 'all' }"
          @click="filterType = 'all'"
        >
          Todos los viajes
        </button>
        <button
          class="tab"
          :class="{ 'tab-active': filterType === 'customizable' }"
          @click="filterType = 'customizable'"
        >
          Personalizables
        </button>
        <button
          class="tab"
          :class="{ 'tab-active': filterType === 'fixed' }"
          @click="filterType = 'fixed'"
        >
          Itinerario fijo
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="trip in filteredTrips"
          :key="trip.id"
          class="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow"
        >
          <figure v-if="trip.featured_image" class="h-48">
            <img
              :src="`/api/images/${trip.featured_image}`"
              :alt="trip.name"
              class="w-full h-full object-cover"
            />
          </figure>
          <div class="card-body">
            <h2 class="card-title">{{ trip.name }}</h2>
            <p class="text-base-content/70 line-clamp-3">
              {{ trip.description }}
            </p>

            <div class="flex items-center gap-2 mt-2">
              <div class="badge badge-primary">
                {{ trip.segments?.length || 0 }} días
              </div>
              <div
                class="badge"
                :class="trip.customizable ? 'badge-accent' : 'badge-secondary'"
              >
                {{ trip.customizable ? "Personalizable" : "Itinerario fijo" }}
              </div>
              <div v-if="trip.price" class="badge badge-success">
                {{ formatPrice(trip.price) }}€
              </div>
            </div>

            <!-- Different descriptions based on trip type -->
            <div class="mt-3 text-sm text-base-content/60">
              <div v-if="trip.customizable" class="flex items-center gap-2">
                <Icon name="heroicons:cog-6-tooth" class="w-4" />
                <span>Personaliza alojamientos, actividades y estancias</span>
              </div>
              <div v-else class="flex items-center gap-2">
                <Icon name="heroicons:shield-check" class="w-4" />
                <span>Experiencia curada por nuestros expertos</span>
              </div>
            </div>

            <div class="card-actions justify-end mt-4">
              <NuxtLink
                :to="
                  trip.customizable
                    ? `/trips/customize/form?trip=${trip.id}`
                    : `/trips/customize/simple?trip=${trip.id}`
                "
                class="btn btn-primary"
              >
                <template v-if="trip.customizable">
                  Personalizar viaje
                  <Icon name="heroicons:cog-6-tooth" class="ml-2" />
                </template>
                <template v-else>
                  Ver detalles
                  <Icon name="heroicons:eye" class="ml-2" />
                </template>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const trips = ref([]);
const loading = ref(true);
const filterType = ref("all");

const filteredTrips = computed(() => {
  if (filterType.value === "all") {
    return trips.value;
  } else if (filterType.value === "customizable") {
    return trips.value.filter((trip) => trip.customizable);
  } else if (filterType.value === "fixed") {
    return trips.value.filter((trip) => !trip.customizable);
  }
  return trips.value;
});

async function fetchAllTrips() {
  try {
    loading.value = true;
    const data = await $fetch("/api/trips");
    trips.value = data || [];
  } catch (error) {
    console.error("Error fetching trips:", error);
    trips.value = [];
  } finally {
    loading.value = false;
  }
}

function formatPrice(price) {
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
}

onMounted(fetchAllTrips);

// Set page meta
useHead({
  title: "Viajes - Selecciona tu aventura",
  meta: [
    {
      name: "description",
      content:
        "Descubre nuestros viajes únicos. Elige entre experiencias personalizables o itinerarios cuidadosamente diseñados por nuestros expertos.",
    },
  ],
});
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
